import {
  collection,
  addDoc,
  serverTimestamp
} from 'firebase/firestore'
import { db, getActiveDb } from '../config/firebase'
import { auth, getActiveAuth } from '../config/firebase'
import { transformKeysToSnake } from '../utils/namingConventions'

export enum LogEventType {
  // Profile operations
  PROFILE_CREATED = 'profile_created',
  PROFILE_DELETED = 'profile_deleted',
  PROFILE_UPDATED = 'profile_updated',
  PROFILE_VIEWED = 'profile_viewed',
  
  // Data operations
  RECORD_CREATED = 'record_created',
  RECORD_DELETED = 'record_deleted',
  RECORD_UPDATED = 'record_updated',
  RECORD_VIEWED = 'record_viewed',
  DATA_ACCESSED = 'data_accessed',
  
  // Security events
  USER_LOGIN = 'user_login',
  USER_LOGOUT = 'user_logout',
  PASSWORD_CHANGED = 'password_changed',
  PERMISSION_CHANGED = 'permission_changed',
  
  // Account operations
  ACCOUNT_CREATED = 'account_created',
  ACCOUNT_DELETED = 'account_deleted',
  ACCOUNT_UPDATED = 'account_updated',
  
  // Genetic data operations
  GENETIC_DATA_UPLOADED = 'genetic_data_uploaded',
  GENETIC_DATA_DELETED = 'genetic_data_deleted',
  GENETIC_DATA_PROCESSING_STARTED = 'genetic_data_processing_started',
  GENETIC_DATA_PROCESSING_COMPLETED = 'genetic_data_processing_completed',
  GENETIC_DATA_PROCESSING_FAILED = 'genetic_data_processing_failed',
  GENETIC_REPORT_GENERATED = 'genetic_report_generated',
  GENETIC_REPORT_VIEWED = 'genetic_report_viewed',
  
  // Health records
  HEALTH_RECORD_CREATED = 'health_record_created',
  HEALTH_RECORD_DELETED = 'health_record_deleted',
  HEALTH_RECORD_UPDATED = 'health_record_updated',
  HEALTH_RECORD_VIEWED = 'health_record_viewed',
  SENSITIVE_DATA_ACCESSED = 'sensitive_data_accessed',
  
  // Wellness goals
  WELLNESS_GOAL_CREATED = 'wellness_goal_created',
  WELLNESS_GOAL_UPDATED = 'wellness_goal_updated',
  WELLNESS_GOAL_DELETED = 'wellness_goal_deleted',
  WELLNESS_GOAL_CHECKIN_STARTED = 'wellness_goal_checkin_started',
  WELLNESS_GOAL_CHECKIN_COMPLETED = 'wellness_goal_checkin_completed',
  WELLNESS_GOAL_COMPLETED = 'wellness_goal_completed',
  
  // Chat and AI interactions
  CHAT_CONVERSATION_STARTED = 'chat_conversation_started',
  CHAT_MESSAGE_SENT = 'chat_message_sent',
  CHAT_MESSAGE_RECEIVED = 'chat_message_received',
  AI_TOOL_USED = 'ai_tool_used',
  AI_RESPONSE_GENERATED = 'ai_response_generated',
  
  // Document operations
  DOCUMENT_UPLOADED = 'document_uploaded',
  DOCUMENT_VIEWED = 'document_viewed',
  DOCUMENT_DOWNLOADED = 'document_downloaded',
  DOCUMENT_DELETED = 'document_deleted',
  
  // Billing operations
  SUBSCRIPTION_CHANGED = 'subscription_changed',
  PAYMENT_PROCESSED = 'payment_processed',
  PAYMENT_FAILED = 'payment_failed',
  
  // User experience
  FEATURE_USED = 'feature_used',
  ERROR_ENCOUNTERED = 'error_encountered',
  PERFORMANCE_ISSUE = 'performance_issue'
}

export interface LogEvent {
  eventType: LogEventType
  userId: string
  profileId?: string
  resourceType?: string
  resourceId?: string
  action?: string
  details?: Record<string, any>
  ipAddress?: string
  userAgent?: string
  timestamp: Date
}

export class LoggingService {
  private readonly COLLECTION_NAME = 'audit_logs'
  
  /**
   * Log a critical event for audit and security purposes
   * Only logs operations that matter for compliance and security
   */
  async logEvent(event: Omit<LogEvent, 'timestamp'>): Promise<void> {
    try {
      const activeAuth = getActiveAuth()
      if (!activeAuth.currentUser) {
        console.warn('Cannot log event: user not authenticated')
        return
      }

      const logEntry = {
        ...event,
        timestamp: serverTimestamp()
      }

      // Add to Firestore audit collection
      const auditRef = collection(getActiveDb(), this.COLLECTION_NAME)
      await addDoc(auditRef, transformKeysToSnake(logEntry))

      // Also log to profile-specific activity log if profileId is provided
      if (event.profileId) {
        await this.logToProfileActivity(event.profileId, event)
      }

    } catch (error) {
      // Never fail the main operation due to logging failure
      console.error('Failed to log audit event:', error)
    }
  }

  /**
   * Log to profile-specific activity log for user-facing audit trail
   */
  private async logToProfileActivity(profileId: string, event: Omit<LogEvent, 'timestamp'>): Promise<void> {
    try {
      const activityRef = collection(getActiveDb(), `profiles/${profileId}/activity_log`)
      // Build details while omitting undefined values (Firestore doesn't allow undefined)
      const baseDetails: Record<string, any> = {
        eventType: event.eventType,
        ...(event.details || {})
      }
      if (event.resourceType !== undefined) baseDetails.resourceType = event.resourceType
      if (event.resourceId !== undefined) baseDetails.resourceId = event.resourceId

      // Shallow-clean undefined from details
      const cleanDetails: Record<string, any> = {}
      for (const [k, v] of Object.entries(baseDetails)) {
        if (v !== undefined) cleanDetails[k] = v
      }

      const activityEntry = {
        userId: event.userId,
        userName: getActiveAuth().currentUser?.displayName || 'Unknown',
        action: event.action || event.eventType,
        details: cleanDetails,
        timestamp: serverTimestamp()
      }
      
      await addDoc(activityRef, transformKeysToSnake(activityEntry))
    } catch (error) {
      console.error('Failed to log to profile activity:', error)
    }
  }

  // Convenience methods for common operations
  
  async logProfileCreated(profileId: string, profileName: string): Promise<void> {
    await this.logEvent({
      eventType: LogEventType.PROFILE_CREATED,
      userId: getActiveAuth().currentUser!.uid,
      profileId,
      resourceType: 'profile',
      resourceId: profileId,
      action: 'profile_created',
      details: { profileName }
    })
  }

  async logProfileDeleted(profileId: string, profileName: string): Promise<void> {
    await this.logEvent({
      eventType: LogEventType.PROFILE_DELETED,
      userId: getActiveAuth().currentUser!.uid,
      profileId,
      resourceType: 'profile',
      resourceId: profileId,
      action: 'profile_deleted',
      details: { profileName }
    })
  }

  async logRecordCreated(profileId: string, recordType: string, recordId: string, recordName?: string): Promise<void> {
    await this.logEvent({
      eventType: LogEventType.RECORD_CREATED,
      userId: getActiveAuth().currentUser!.uid,
      profileId,
      resourceType: recordType,
      resourceId: recordId,
      action: 'record_created',
      details: { recordName, recordType }
    })
  }

  async logRecordDeleted(profileId: string, recordType: string, recordId: string, recordName?: string): Promise<void> {
    await this.logEvent({
      eventType: LogEventType.RECORD_DELETED,
      userId: getActiveAuth().currentUser!.uid,
      profileId,
      resourceType: recordType,
      resourceId: recordId,
      action: 'record_deleted',
      details: { recordName, recordType }
    })
  }

  async logDataAccess(profileId: string, dataType: string, accessReason?: string): Promise<void> {
    await this.logEvent({
      eventType: LogEventType.DATA_ACCESSED,
      userId: getActiveAuth().currentUser!.uid,
      profileId,
      resourceType: dataType,
      action: 'data_accessed',
      details: { accessReason }
    })
  }

  async logSecurityEvent(eventType: LogEventType, details?: Record<string, any>): Promise<void> {
    await this.logEvent({
      eventType,
      userId: getActiveAuth().currentUser!.uid,
      action: eventType,
      details
    })
  }

  async logGeneticDataOperation(
    eventType: LogEventType.GENETIC_DATA_UPLOADED | LogEventType.GENETIC_DATA_DELETED,
    profileId: string,
    uploadId: string,
    fileType?: string
  ): Promise<void> {
    // Clean details object to remove undefined values
    const details: Record<string, any> = { uploadId }
    if (fileType) {
      details.fileType = fileType
    }
    
    await this.logEvent({
      eventType,
      userId: getActiveAuth().currentUser!.uid,
      profileId,
      resourceType: 'genetic_data',
      resourceId: uploadId,
      action: eventType,
      details
    })
  }

  async logAccountDeleted(userId: string, details?: Record<string, any>): Promise<void> {
    await this.logEvent({
      eventType: LogEventType.ACCOUNT_DELETED,
      userId,
      action: 'account_deleted',
      details
    })
  }
}

// Export singleton instance
export const loggingService = new LoggingService()
